import React, { useState } from 'react'
import { Box, Flex, Link, Text, TextField } from 'components/ds'
import Label from '../label'
import LoadingButton from '../loading-button'
import type { FormEvent } from 'react'
import Warning from '../warning'
export type SamlDiscoveryPayload = {
  redirect_uri?: string
}
type Props = {
  onSubmit: (email: string) => Promise<SamlDiscoveryPayload>
  onDismiss?: () => void
}

/**
 * SamlDiscoveryForm component for SSO email discovery
 *
 * Allows users to enter their email address to discover their SSO provider.
 * The form submits the email to determine if SSO is enabled for the user's domain
 * and redirects to the appropriate SSO login page.
 *
 * @param props - Component props
 * @param props.onSubmit - Callback function that processes the email and returns SSO discovery payload
 * @param props.onDismiss - Optional callback function to dismiss the form and return to login
 * @returns JSX element containing the SSO discovery form
 */
const SamlDiscoveryForm = ({ onSubmit, onDismiss }: Props): React.ReactNode => {
  const [error, setError] = useState<React.ReactNode | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Get the form data
    const formData = new FormData(e.target as HTMLFormElement)
    const email = formData.get('email') || ''
    // Ensure the values are strings
    if (typeof email !== 'string') throw new Error('Invalid email field')
    // if (!valid) return
    setSubmitting(true)

    try {
      const result = await onSubmit(email.trim())

      if (result.redirect_uri) {
        window.location.href = result.redirect_uri
      } else {
        setError(
          <>
            <Text as="span">SSO is not enabled for your DISCO.</Text>
            {/* TODO: Add a link to the help center
            <p>Find out more</p> */}
          </>,
        )
      }
    } catch (error) {
      setError('An error occurred while sending the SSO request.')
    }

    setSubmitting(false)
  }

  return (
    <Flex asChild direction="column" gap="3">
      <form onSubmit={handleSubmit}>
        <Box>
          <Label htmlFor="email" caption="Your email" />
          <TextField.Root
            size="3"
            id="email"
            name="email"
            type="text"
            defaultValue=""
            placeholder="you@company.com"
            autoComplete="email"
            autoCapitalize="off"
            disabled={submitting} // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </Box>
        <Flex direction="column" gap="2">
          <LoadingButton
            type="submit"
            size="3"
            color="gray"
            variant="solid"
            highContrast
            loading={submitting}
            loadingLabel="Sending…"
            data-testid="button-primary"
          >
            Continue
          </LoadingButton>
          {error && <Warning>{error}</Warning>}
          {onDismiss && (
            <Flex direction="column" gap="2" mt="2">
              <Text align="center" size="2">
                <Link
                  href="#"
                  onClick={e => {
                    e.preventDefault()
                    onDismiss()
                  }}
                >
                  Back to Sign in
                </Link>
              </Text>
            </Flex>
          )}
        </Flex>
      </form>
    </Flex>
  )
}

export default SamlDiscoveryForm
